import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';

/** Escopo de visibilidade de appliances. "none" só é usado quando `includeNone`. */
export type EscopoAppliance = 'all' | 'specific' | 'none';

type ApplianceOption = { id: number; nome: string };

type EscopoOption = { value: EscopoAppliance; label: string; desc: string };

const ESCOPO_OPTIONS: EscopoOption[] = [
    { value: 'all', label: 'Todos os appliances', desc: 'Grupo enxerga qualquer appliance (padrão)' },
    { value: 'specific', label: 'Appliances específicos', desc: 'Restringir a uma lista de appliances' },
    { value: 'none', label: 'Nenhum appliance', desc: 'Grupo desativado em todos os appliances' },
];

/**
 * Seletor reutilizável de escopo de appliances (Todas / Específicas / [Nenhum]).
 * Encapsula a busca de appliances e a lista de seleção. Usado tanto pelos grupos
 * RADIUS (`includeNone`) quanto pelos grupos/departamentos de usuários do sistema.
 */
export function SeletorEscopoAppliances({
    scope,
    onScopeChange,
    selectedIds,
    onSelectedIdsChange,
    includeNone = false,
    label = 'Appliances permitidos',
}: {
    scope: EscopoAppliance;
    onScopeChange: (scope: EscopoAppliance) => void;
    selectedIds: number[];
    onSelectedIdsChange: (ids: number[]) => void;
    includeNone?: boolean;
    label?: string;
}) {
    const uid = React.useId();
    const options = includeNone
        ? ESCOPO_OPTIONS
        : ESCOPO_OPTIONS.filter((o) => o.value !== 'none');

    const queryAppliances = useQuery(
        ['appliances-list-scope'],
        async () => {
            const res = await axios.get<{ ok: boolean; result: ApplianceOption[] }>(
                '/api/appliance?limit=200',
                { withCredentials: true },
            );
            return (res.data.result ?? []) as ApplianceOption[];
        },
        { staleTime: 60000 },
    );

    const appliances = queryAppliances.data ?? [];

    const toggleAppliance = (id: number) => {
        onSelectedIdsChange(
            selectedIds.includes(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id],
        );
    };

    return (
        <VBox style={{ gap: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-label)' }}>
                {label}
            </label>
            {options.map((opt) => (
                <HBox key={opt.value} style={{ gap: '8px', alignItems: 'center' }}>
                    <input
                        type="radio"
                        id={`scope-${uid}-${opt.value}`}
                        name={`appliance_scope-${uid}`}
                        checked={scope === opt.value}
                        onChange={() => onScopeChange(opt.value)}
                    />
                    <label
                        htmlFor={`scope-${uid}-${opt.value}`}
                        style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}
                    >
                        {opt.label}
                    </label>
                    <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>{opt.desc}</span>
                </HBox>
            ))}

            {scope === 'specific' && (
                <VBox
                    style={{
                        marginTop: '6px',
                        paddingLeft: '24px',
                        gap: '4px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                        border: '1px solid var(--tc-color-border)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                    }}
                >
                    {queryAppliances.isLoading && (
                        <span style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                            Carregando appliances...
                        </span>
                    )}
                    {appliances.length === 0 && !queryAppliances.isLoading && (
                        <span style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                            Nenhum appliance encontrado
                        </span>
                    )}
                    {appliances.map((ap) => (
                        <HBox key={ap.id} style={{ gap: '8px', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                id={`ap-${uid}-${ap.id}`}
                                checked={selectedIds.includes(ap.id)}
                                onChange={() => toggleAppliance(ap.id)}
                            />
                            <label
                                htmlFor={`ap-${uid}-${ap.id}`}
                                style={{ cursor: 'pointer', userSelect: 'none', fontSize: '13px' }}
                            >
                                {ap.nome}
                            </label>
                        </HBox>
                    ))}
                </VBox>
            )}
        </VBox>
    );
}

export default SeletorEscopoAppliances;
